import { useQuery } from '@tanstack/react-query';
import type { TargetTypeType } from '@terra/shared-schemas-orchestrator';
import { DETECTION_VIEW } from '@terra/ui-design-system';
import { fetchTorchApplicationMetrics } from './useKpiMetrics';
import { fetchNeoExecutionsCount } from './neo-executions-count';

export type SinceLastLoginData = {
  signals: {
    total: number;
  };
  findings: {
    total: number;
    critical: number;
    high: number;
    medium: number;
    low: number;
  };
  executions: {
    total: number;
  };
  assets: {
    total: number;
  };
};

const EMPTY_DATA: SinceLastLoginData = {
  signals: { total: 0 },
  findings: { total: 0, critical: 0, high: 0, medium: 0, low: 0 },
  executions: { total: 0 },
  assets: { total: 0 },
};

export function useSinceLastLogin({
  applicationId,
  targetType,
  since,
}: {
  applicationId: string;
  targetType: TargetTypeType;
  since: Date | null;
}) {
  return useQuery({
    queryKey: [
      'since-last-visit',
      applicationId,
      targetType,
      since?.toISOString(),
      DETECTION_VIEW.ALL,
    ],
    queryFn: async (): Promise<SinceLastLoginData> => {
      if (!since) {
        return EMPTY_DATA;
      }

      const fromDate = since;
      const toDate = new Date();

      const [metrics, executionsTotal] = await Promise.all([
        fetchTorchApplicationMetrics({
          applicationId,
          from: fromDate,
          to: toDate,
        }),
        fetchNeoExecutionsCount({ applicationId, startDate: fromDate }),
      ]);

      return {
        signals: {
          total: metrics?.signals?.total ?? 0,
        },
        findings: {
          total: metrics?.findings?.total ?? 0,
          critical: metrics?.findings?.bySeverity?.critical ?? 0,
          high: metrics?.findings?.bySeverity?.high ?? 0,
          medium: metrics?.findings?.bySeverity?.medium ?? 0,
          low: metrics?.findings?.bySeverity?.low ?? 0,
        },
        executions: {
          total: executionsTotal,
        },
        assets: {
          total: metrics?.assets?.total ?? 0,
        },
      };
    },
    enabled: !!applicationId && !!since,
    staleTime: 30_000,
  });
}
